/**
 * AI Console hooks for data fetching and mutations.
 *
 * Uses @tanstack/react-query for caching and state management.
 * Talks to /v1/ai-console/* and /v1/feedback/* endpoints.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AXIOS_INSTANCE } from '../services/api-client'
import type {
  DashboardMetrics,
  DashboardFilters,
  AppletSummary,
  AppletTraceMetrics,
  DailyMetrics,
  AppletAgentMapping,
  TraceListItem,
  TraceDetail,
  SandboxRunRequest,
  SandboxRunResponse,
  SandboxDraft,
  PromptVersion,
  RecordFeedbackRequest,
  FeedbackSummary,
  IterationReadiness,
  EvalSet,
  EvalCase,
  EvalRun,
  EvalCaseResult,
} from '../pages/ai-console/types'

const BASE = '/v1/ai-console'
const FEEDBACK_BASE = '/v1/feedback'
const RAG_EVAL_BASE = '/api/v1/rag-eval'

function appletToEvalDomain(appletId?: string): string | undefined {
  if (!appletId) return undefined
  const mapping: Record<string, string> = {
    business_intelligence: 'fasb',
    chat: 'fasb',
  }
  return mapping[appletId]
}

// ── Dashboard ──────────────────────────────────────────────────────

export function useDashboardMetrics(filters: DashboardFilters) {
  return useQuery<DashboardMetrics>({
    queryKey: ['ai-console', 'dashboard', 'metrics', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.applet_id) params.set('applet_id', filters.applet_id)
      if (filters.user_id) params.set('user_id', filters.user_id)
      if (filters.model_id) params.set('model_id', filters.model_id)
      params.set('period_days', String(filters.period_days))
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/dashboard/metrics?${params}`)
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useAppletSummaries() {
  return useQuery<AppletSummary[]>({
    queryKey: ['ai-console', 'dashboard', 'applets'],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/dashboard/applets`)
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useAppletTraceMetrics(periodDays: number = 7) {
  return useQuery<AppletTraceMetrics[]>({
    queryKey: ['ai-console', 'dashboard', 'applet-metrics', periodDays],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/dashboard/applet-metrics?period_days=${periodDays}`)
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useDailyMetrics(periodDays: number = 7, appletId?: string) {
  return useQuery<DailyMetrics[]>({
    queryKey: ['ai-console', 'dashboard', 'daily-metrics', periodDays, appletId],
    queryFn: async () => {
      const params = new URLSearchParams({ period_days: String(periodDays) })
      if (appletId) params.set('applet_id', appletId)
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/dashboard/daily-metrics?${params}`)
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

// ── Mappings ───────────────────────────────────────────────────────

export function useAppletMappings(appletId: string | undefined) {
  return useQuery<AppletAgentMapping[]>({
    queryKey: ['ai-console', 'mappings', appletId],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/mappings/${appletId}`)
      return data
    },
    enabled: !!appletId,
    staleTime: 10 * 60 * 1000,
  })
}

// ── Traces ─────────────────────────────────────────────────────────

export function useTraces(params: {
  applet_id: string
  data_element_key?: string
  agent_identifier?: string
  limit?: number
}) {
  return useQuery<TraceListItem[]>({
    queryKey: ['ai-console', 'traces', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      searchParams.set('applet_id', params.applet_id)
      if (params.data_element_key) searchParams.set('data_element_key', params.data_element_key)
      if (params.agent_identifier) searchParams.set('agent_identifier', params.agent_identifier)
      if (params.limit) searchParams.set('limit', String(params.limit))
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/traces?${searchParams}`)
      return data
    },
    enabled: !!params.applet_id,
    staleTime: 60 * 1000,
  })
}

export function useTraceDetail(traceId: string | undefined) {
  return useQuery<TraceDetail>({
    queryKey: ['ai-console', 'trace', traceId],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/traces/${traceId}`)
      return data
    },
    enabled: !!traceId,
    staleTime: 60 * 1000,
  })
}

// ── Prompt Versions ────────────────────────────────────────────────

export function usePromptVersions(promptName: string | undefined) {
  return useQuery<PromptVersion[]>({
    queryKey: ['ai-console', 'prompts', promptName],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/prompts/${promptName}/versions`)
      return data
    },
    enabled: !!promptName,
    staleTime: 5 * 60 * 1000,
  })
}

// ── Sandbox ────────────────────────────────────────────────────────

export function useSandboxRun() {
  return useMutation<SandboxRunResponse, Error, SandboxRunRequest>({
    mutationFn: async (request) => {
      const { data } = await AXIOS_INSTANCE.post(`${BASE}/sandbox/run`, request)
      return data
    },
  })
}

export function useSandboxDrafts(agentIdentifier?: string) {
  return useQuery<SandboxDraft[]>({
    queryKey: ['ai-console', 'sandbox', 'drafts', agentIdentifier],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (agentIdentifier) params.set('agent_identifier', agentIdentifier)
      const qs = params.toString()
      const { data } = await AXIOS_INSTANCE.get(`${BASE}/sandbox/drafts${qs ? `?${qs}` : ''}`)
      return data
    },
    staleTime: 30 * 1000,
  })
}

export function useSaveSandboxDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (draft: Omit<SandboxDraft, 'id' | 'customer_id' | 'status' | 'created_by' | 'created_at' | 'updated_at'>) => {
      const { data } = await AXIOS_INSTANCE.post(`${BASE}/sandbox/drafts`, draft)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-console', 'sandbox', 'drafts'] })
    },
  })
}

export function useDeleteSandboxDraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (draftId: number) => {
      await AXIOS_INSTANCE.delete(`${BASE}/sandbox/drafts/${draftId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-console', 'sandbox', 'drafts'] })
    },
  })
}

// ── Feedback ───────────────────────────────────────────────────────

export function useRecordFeedback() {
  return useMutation({
    mutationFn: async (request: RecordFeedbackRequest) => {
      const { data } = await AXIOS_INSTANCE.post(`${FEEDBACK_BASE}/`, request)
      return data
    },
  })
}

export function useFeedbackSummary(params: {
  agent_identifier?: string
  applet_id?: string
  days?: number
}) {
  return useQuery<FeedbackSummary>({
    queryKey: ['feedback', 'summary', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      if (params.agent_identifier) searchParams.set('agent_identifier', params.agent_identifier)
      if (params.applet_id) searchParams.set('applet_id', params.applet_id)
      if (params.days) searchParams.set('days', String(params.days))
      const { data } = await AXIOS_INSTANCE.get(`${FEEDBACK_BASE}/summary?${searchParams}`)
      return data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useIterationReadiness(agentIdentifier: string | undefined, promptVersion?: number) {
  return useQuery<IterationReadiness>({
    queryKey: ['feedback', 'readiness', agentIdentifier, promptVersion],
    queryFn: async () => {
      const params = promptVersion ? `?prompt_version=${promptVersion}` : ''
      const { data } = await AXIOS_INSTANCE.get(`${FEEDBACK_BASE}/readiness/${agentIdentifier}${params}`)
      return data
    },
    enabled: !!agentIdentifier,
  })
}

// ── Eval Sets ─────────────────────────────────────────────────────

export function useEvalSets(params?: { applet_id?: string; agent_identifier?: string }) {
  return useQuery<EvalSet[]>({
    queryKey: ['ai-console', 'eval-sets', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      const domain = appletToEvalDomain(params?.applet_id)
      if (!domain) return []
      searchParams.set('domain', domain)
      const qs = searchParams.toString()
      const { data } = await AXIOS_INSTANCE.get(`${RAG_EVAL_BASE}/eval-sets${qs ? `?${qs}` : ''}`)
      return (data.eval_sets || []).map((evalSet: Record<string, unknown>) => ({
        id: Number(evalSet.id),
        customer_id: String(evalSet.customer_id || ''),
        name: String(evalSet.name || ''),
        description: typeof evalSet.description === 'string' ? evalSet.description : undefined,
        applet_id: params?.applet_id || domain,
        agent_identifier: params?.agent_identifier || domain,
        scope_level: 'agent' as const,
        case_count: Number(evalSet.example_count || 0),
        created_at: typeof evalSet.created_at === 'string' ? evalSet.created_at : undefined,
        updated_at: typeof evalSet.updated_at === 'string' ? evalSet.updated_at : undefined,
      }))
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useEvalSetCases(evalSetId: number | undefined) {
  return useQuery<EvalCase[]>({
    queryKey: ['ai-console', 'eval-sets', evalSetId, 'cases'],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${RAG_EVAL_BASE}/eval-sets/${evalSetId}/questions?limit=200`)
      return (data.questions || []).map((question: Record<string, unknown>, index: number) => ({
        id: index + 1,
        eval_set_id: evalSetId || 0,
        label: typeof question.eval_id === 'string' ? question.eval_id : undefined,
        input: {
          question: question.question,
          difficulty: question.difficulty,
        },
        expected_output: typeof question.reference_answer === 'string' ? question.reference_answer : undefined,
        tags: Array.isArray(question.tags) ? question.tags.map(String) : undefined,
      }))
    },
    enabled: !!evalSetId,
  })
}

export function useCreateEvalSet() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      throw new Error('Eval set creation is handled in the dedicated /evals workspace.')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-console', 'eval-sets'] })
    },
  })
}

export function useDeleteEvalSet() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (evalSetId: number) => {
      await AXIOS_INSTANCE.delete(`${RAG_EVAL_BASE}/eval-sets/${evalSetId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-console', 'eval-sets'] })
    },
  })
}

// ── Eval Runs ─────────────────────────────────────────────────────

export function useEvalRuns(params?: { eval_set_id?: number; agent_identifier?: string; limit?: number; applet_id?: string }) {
  return useQuery<EvalRun[]>({
    queryKey: ['ai-console', 'eval-runs', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      const domain = appletToEvalDomain(params?.applet_id)
      if (!domain) return []
      searchParams.set('domain', domain)
      searchParams.set('page_size', String(params?.limit || 20))
      const qs = searchParams.toString()
      const { data } = await AXIOS_INSTANCE.get(`${RAG_EVAL_BASE}/runs${qs ? `?${qs}` : ''}`)
      return (data.runs || []).map((run: Record<string, unknown>) => {
        const metrics = (run.metrics || {}) as Record<string, unknown>
        return {
          id: String(run.run_id || ''),
          db_id: Number(run.id),
          eval_set_id: Number(run.eval_set_id || 0),
          eval_set_name: typeof run.eval_set_name === 'string' ? run.eval_set_name : undefined,
          agent_identifier: params?.agent_identifier || domain,
          model_id: typeof run.chat_model === 'string' ? run.chat_model : undefined,
          status: String(run.status || 'pending') as EvalRun['status'],
          total_cases: Number(metrics.total_questions || run.sample_size || 0),
          passed: Number(metrics.pass_count || 0),
          failed: Number(metrics.fail_count || 0),
          avg_latency_ms: undefined,
          total_cost_usd: undefined,
          started_at: typeof run.started_at === 'string' ? run.started_at : undefined,
          completed_at: typeof run.completed_at === 'string' ? run.completed_at : undefined,
          semantic_suite_config:
            typeof run.semantic_suite_config === 'object' && run.semantic_suite_config !== null
              ? run.semantic_suite_config as Record<string, unknown>
              : undefined,
          semantic_suite_evaluation:
            typeof run.semantic_suite_evaluation === 'object' && run.semantic_suite_evaluation !== null
              ? run.semantic_suite_evaluation as Record<string, unknown>
              : undefined,
        }
      })
    },
    staleTime: 30 * 1000,
  })
}

export function useEvalRunResults(runId: string | undefined) {
  return useQuery<EvalCaseResult[]>({
    queryKey: ['ai-console', 'eval-runs', runId, 'results'],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`${RAG_EVAL_BASE}/runs/${runId}?include_results=true&results_page_size=200`)
      return (data.results || []).map((result: Record<string, unknown>) => ({
        id: Number(result.id),
        eval_run_id: String(runId),
        eval_case_id: Number(result.question_index || 0),
        case_label: typeof result.eval_id === 'string' ? result.eval_id : undefined,
        input: { question: result.question },
        expected_output: typeof result.expected_answer === 'string' ? result.expected_answer : undefined,
        actual_output: typeof result.model_response === 'string' ? result.model_response : undefined,
        passed: result.verdict === 'pass',
        latency_ms: Number(result.processing_time_ms || 0),
        score: typeof result.factual_correctness === 'number' ? result.factual_correctness : undefined,
        error_message: result.verdict === 'error' && typeof result.verdict_reason === 'string' ? result.verdict_reason : undefined,
        structured_trace: typeof result.structured_trace === 'object' && result.structured_trace !== null
          ? result.structured_trace as Record<string, unknown>
          : undefined,
        assertion_results: Array.isArray(result.assertion_results)
          ? result.assertion_results as Array<Record<string, unknown>>
          : undefined,
        provenance: typeof result.provenance === 'object' && result.provenance !== null
          ? result.provenance as Record<string, unknown>
          : undefined,
        pack_id: typeof result.pack_id === 'number' || typeof result.pack_id === 'string'
          ? result.pack_id
          : undefined,
        pack_version: typeof result.pack_version === 'number' || typeof result.pack_version === 'string'
          ? result.pack_version
          : undefined,
      }))
    },
    enabled: !!runId,
  })
}

export function useStartEvalRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (request: {
      eval_set_id: number
      workspace_id: number
      domain: string
      eval_type?: 'rag_full' | 'semantic_regression'
      model_id?: string
      config_overrides?: Record<string, unknown>
    }) => {
      const { data } = await AXIOS_INSTANCE.post(`${RAG_EVAL_BASE}/runs/v2`, {
        domain: request.domain,
        workspace_id: request.workspace_id,
        eval_type: request.eval_type || 'rag_full',
        eval_source: 'eval_set',
        eval_set_id: request.eval_set_id,
        concurrency: 3,
        validate_citations: false,
      })
      return {
        id: String(data.run_id),
        eval_set_id: request.eval_set_id,
        agent_identifier: request.domain,
        status: String(data.status || 'pending') as EvalRun['status'],
        total_cases: 0,
        passed: 0,
        failed: 0,
      } as EvalRun
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-console', 'eval-runs'] })
      queryClient.invalidateQueries({ queryKey: ['ai-console', 'eval-sets'] })
    },
  })
}
